import React, { FC, memo, useEffect, useRef, useState } from 'react';
import SingleInputItem from './item/SingleInputItem';
import { Dirary } from '../../../types/dirary';
import classes from './SingleInputDetail.module.scss';
import { useTheme } from '@mui/material';
import { Buttons } from '../../uikit/atoms/buttons/Buttons';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
//追加
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ScrollButton from '../../uikit/atoms/buttons/ScrollButton';
import { useNavigate } from 'react-router-dom';
import { useAddPosts } from '../../../hooks/api/posts/useAddPosts';
import { useDeletePosts } from '../../../hooks/api/posts/useDeletePosts';
import { useUpdatePosts } from '../../../hooks/api/posts/useUpdatePosts';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  diaryListState,
  diaryListStateForMatrix,
} from '../../../states/diaryListState';
import { postIdCounterState } from '../../../states/postIdCounterState';
import {
  fetchManageStateForList,
  fetchManageStateForMatrix,
} from '../../../states/fetchMnageState';
import { useSubmitGuard } from '../../../hooks/util/useSubmitGuard';

type Props = {
  diaryList: Array<Dirary>;
  addDiaryList: Array<Dirary>;
  deleteDiaryList: Array<Dirary>;
  updateDiaryList: Array<Dirary>;
  setDiaryList: React.Dispatch<React.SetStateAction<Array<Dirary>>>;
  defaultDiary: Dirary;
  isSendable: boolean;
  totalTime: { hour: number; min: number; text: string };
  setTotalTime: React.Dispatch<
    React.SetStateAction<{ hour: number; min: number; text: string }>
  >;
  setIsShow?: React.Dispatch<React.SetStateAction<boolean>>;
  fromOtherPage?: boolean;
  callFrom?: string;
  fromTime: string;
  shiftType: number; //夜勤2直対応: Main→Item へ中継
  isOverMaxChain: boolean; //夜勤2直対応(brush-up): 24h超で赤アラート表示
  postIdCounter: number;
  setPostIdCounter: React.Dispatch<React.SetStateAction<number>>;
};
const SingleInputDetail: FC<Props> = memo((props) => {
  const navigation = useNavigate();
  const { addPosts } = useAddPosts();
  const deletePosts = useDeletePosts();
  const updatePosts = useUpdatePosts();
  const addButtonRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [recoilDairyList, setRecoilDiaryList] =
    useRecoilState<Dirary[]>(diaryListState);

  const [recoilDiaryListForMatrix, setRecoilDiaryListForMatrix] =
    useRecoilState<Dirary[]>(diaryListStateForMatrix);
  //保存成功後にサーバ再取得をトリガ(HeaderController が現在の検索条件で再取得し一覧を権威化)
  const setFetchManageForList = useSetRecoilState(fetchManageStateForList);
  const setFetchManageForMatrix = useSetRecoilState(fetchManageStateForMatrix);
  //二重送信ガード(同期 ref で再入防止 + 送信中フラグ)
  const { isSubmitting, runGuarded } = useSubmitGuard();
  const {
    diaryList,
    setDiaryList,
    totalTime,
    setTotalTime,
    defaultDiary,
    isSendable,
    deleteDiaryList,
    addDiaryList,
    updateDiaryList,
    fromOtherPage = false,
    setIsShow = undefined,
    callFrom = '',
    fromTime,
    shiftType,
    isOverMaxChain,
    postIdCounter,
    setPostIdCounter,
  } = props;
  const [addButtonDisabled, setAddButtonDisabled] = useState(true);

  const theme = useTheme();

  //更新ボタンが押されたときのイベント(二重送信ガードで包む)
  const onSave = () =>
    runGuarded(async () => {
      //1) 書き込み。戻り値で成否判定(null=失敗)。BE の add/update も await 化済のため
      //   200/エラーが正しく返り、delete 同様に成否を判定できる。
      const results: Array<unknown> = [];
      if (addDiaryList.length > 0) results.push(await addPosts(addDiaryList));
      if (updateDiaryList.length > 0)
        results.push(await updatePosts(updateDiaryList));
      if (deleteDiaryList.length > 0)
        results.push(await deletePosts(deleteDiaryList));
      const allOk = results.every((r) => r != null);

      //2) サーバ再取得で一覧を権威化(成否に関わらず真の状態へ揃える)。
      //   callFrom 該当ビューの HeaderController が現在の検索条件で再取得する。
      if (callFrom === 'matrix') {
        setFetchManageForMatrix((v) => !v);
      } else if (callFrom === 'list' || callFrom === 'listByNew') {
        setFetchManageForList((v) => !v);
      }

      //失敗時は閉じない・楽観更新しない(フックが既に alert 済。ユーザが修正して再送信)
      if (!allOk) return;

      //3) 楽観更新(即時反映)。例外で閉じ/遷移を阻害しないよう try で隔離。
      try {
        if (callFrom === 'matrix') {
        setRecoilDiaryListForMatrix((diaryList) => {
          const copyDiaryList = [...diaryList];

          // Update diaries
          updateDiaryList.forEach((updateDiary) => {
            const index = copyDiaryList.findIndex(
              (diary) => diary.id === updateDiary.id
            );
            if (index !== -1) {
              copyDiaryList[index] = updateDiary;
            }
          });
          //追記分（マイナス）を既存のIDに置き換える
          // 負のidを持つ要素のインデックスを取得
          const negativeIndices = copyDiaryList
            .map((diary, index) => (diary.id < 0 ? index : -1))
            .filter((index) => index !== -1);

          // 負のidを持つ要素のインデックスを大きい順にソート
          negativeIndices.sort(
            (a, b) => copyDiaryList[b].id - copyDiaryList[a].id
          );

          // updateDiaryListをidの大きい順にソート
          const sortedUpdateDiaryList = [...updateDiaryList].sort(
            (a, b) => parseInt(b.id as any) - parseInt(a.id as any)
          );

          // 負のidを持つ要素をupdateDiaryListのidの大きい順で置き換え
          negativeIndices.forEach((index, i) => {
            if (i < sortedUpdateDiaryList.length) {
              copyDiaryList[index] = sortedUpdateDiaryList[i];
            }
          });

          // Delete diaries
          deleteDiaryList.forEach((deleteDiary) => {
            const index = copyDiaryList.findIndex(
              (diary) => diary.id === deleteDiary.id
            );
            if (index !== -1) {
              copyDiaryList.splice(index, 1);
            }
          });

          // Add new diaries
          addDiaryList.forEach((newDiary) => {
            const index = copyDiaryList.findIndex(
              (diary) => diary.id === newDiary.id
            );
            if (index === -1) {
              copyDiaryList.push(newDiary);
            }
          });
          // ID 重複を取り除く
          const uniqueDiaryList = Array.from(
            copyDiaryList
              .reduce((map, diary) => {
                map.set(diary.id, diary);
                return map;
              }, new Map<number, Dirary>())
              .values()
          );

          return uniqueDiaryList;
        });
      } else if (callFrom === 'list' || callFrom === 'listByNew') {
        setRecoilDiaryList((diaryList) => {
          const copyDiaryList = [...diaryList];

          // Update diaries
          updateDiaryList.forEach((updateDiary) => {
            const index = copyDiaryList.findIndex(
              (diary) => diary.id === updateDiary.id
            );
            if (index !== -1) {
              copyDiaryList[index] = updateDiary;
            }
          });
          //追記分（マイナス）を既存のIDに置き換える
          // 負のidを持つ要素のインデックスを取得
          const negativeIndices = copyDiaryList
            .map((diary, index) => (diary.id < 0 ? index : -1))
            .filter((index) => index !== -1);

          // 負のidを持つ要素のインデックスを大きい順にソート
          negativeIndices.sort(
            (a, b) => copyDiaryList[b].id - copyDiaryList[a].id
          );

          // updateDiaryListをidの大きい順にソート
          const sortedUpdateDiaryList = [...updateDiaryList].sort(
            (a, b) => parseInt(b.id as any) - parseInt(a.id as any)
          );

          // 負のidを持つ要素をupdateDiaryListのidの大きい順で置き換え
          negativeIndices.forEach((index, i) => {
            if (i < sortedUpdateDiaryList.length) {
              copyDiaryList[index] = sortedUpdateDiaryList[i];
            }
          });

          // Delete diaries
          deleteDiaryList.forEach((deleteDiary) => {
            const index = copyDiaryList.findIndex(
              (diary) => diary.id === deleteDiary.id
            );
            if (index !== -1) {
              copyDiaryList.splice(index, 1);
            }
          });

          // Add new diaries
          addDiaryList.forEach((newDiary) => {
            const index = copyDiaryList.findIndex(
              (diary) => diary.id === newDiary.id
            );
            if (index === -1) {
              copyDiaryList.push(newDiary);
            }
          });
          // ID 重複を取り除く
          const uniqueDiaryList = Array.from(
            copyDiaryList
              .reduce((map, diary) => {
                map.set(diary.id, diary);
                return map;
              }, new Map<number, Dirary>())
              .values()
          );

          return uniqueDiaryList;
        });
      }
      } catch (e) {
        //楽観更新の失敗は致命的でない(再取得で整合する)
        console.log('楽観更新に失敗しました(再取得で整合)', e);
      }

      if (fromOtherPage && setIsShow) {
        setIsShow(false);
      } else {
        //成功したらメイン画面を表示する
        navigation('/');
      }
    });
  //追加ボタンが押されたときのイベント
  const handleClickAddButton = () => {
    if (diaryList.length === 0) {
      //日報が１つも存在しない場合
      const newDiary = { ...defaultDiary, id: postIdCounter };
      setDiaryList((prevList) => [...prevList, newDiary]);
      setPostIdCounter((preCounter) => preCounter - 1);
    } else {
      //日報が存在する場合
      const newDiaryList = [...diaryList];
      const newDiary = { ...defaultDiary, id: postIdCounter };
      newDiaryList.push(newDiary);
      setDiaryList(newDiaryList);
      setPostIdCounter((preCounter) => preCounter - 1);
    }
  };
  useEffect(() => {
    if (defaultDiary.workerName !== '') {
      setAddButtonDisabled(false);
    } else {
      setAddButtonDisabled(true);
    }
  }, [defaultDiary]);
  useEffect(() => {
    // 追加ボタンの位置までスクロール
    if (addButtonRef.current) {
      addButtonRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'center',
      });
    } else {
      console.log('addButtonRef.current is null');
    }
  }, [diaryList.length]);
  return (
    <div className={classes.rootContainer}>
      <div className={classes.header}>
        <div className={classes.totalTime}>
          <p>合計作業時間</p>
          <div
            style={{
              color:
                totalTime.hour <= 0
                  ? theme.palette.error.main
                  : theme.palette.foreColor.main,
              // theme.palette.foreColor.main,
            }}
          >
            {totalTime.text}
          </div>
        </div>

        {/* 夜勤2直対応(brush-up): チェーンが24時間を超えると送信不可。理由を赤チップで表示(F5-1) */}
        {isOverMaxChain && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: `1px solid ${theme.palette.error.main}`,
              color: theme.palette.error.main,
              borderRadius: '6px',
              padding: '2px 8px',
              fontSize: '12px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 16 }} />
            24時間を超えています
          </div>
        )}

        <div className={classes.updateButton}>
          <p>更新</p>
          <Buttons
            disabled={!isSendable || isSubmitting}
            onClick={onSave}
            caption={'更新'}
            icon={<SendIcon />}
            width={26}
            height={26}
            fontSize={24}
          />
        </div>
      </div>
      <div className={classes.detail} ref={scrollRef}>
        <div ref={targetRef}></div>
        {diaryList.map((diary, index) => (
          <SingleInputItem
            key={diary.id}
            id={diary.id}
            diaryList={diaryList}
            setDiaryList={setDiaryList}
            setTotalTime={setTotalTime}
            fromTime={fromTime}
            shiftType={shiftType}
          />
        ))}
        <div className={classes.addButton} ref={addButtonRef}>
          <Buttons
            disabled={addButtonDisabled}
            onClick={handleClickAddButton}
            caption={''}
            icon={<AddCircleIcon />}
            width={24}
            height={24}
            fontSize={24}
          />
        </div>
        <ScrollButton targetElm={targetRef} scrollElm={scrollRef} />
      </div>
    </div>
  );
});

export default SingleInputDetail;
